/* <garage-picker> renders the choose-your-vehicle flow.
 *
 * Model, then year, then trim. No Make step: this store sells for two
 * platforms, so asking "Ford?" is a question with one answer, and two large
 * targets beat a dropdown for the first choice. Worth revisiting only if a
 * third platform is ever added.
 *
 * Each step is drawn from the tree already in the page, so nothing waits on a
 * request between taps.
 *
 * The element owns the step state and nothing else does. The dialog's back
 * button lives in the header, above this element in the DOM, so it cannot be
 * rendered here; instead this element exposes back() and announces every step
 * change, and garage-ui.js wires the two together. One owner of the state, one
 * event, no second copy of "which step are we on" to fall out of step.
 */
if (!customElements.get('garage-picker')) {
  customElements.define(
    'garage-picker',
    class GaragePicker extends HTMLElement {
      connectedCallback() {
        if (this.wired) return
        this.wired = true
        this.state = { model: null, year: null }
        this.addEventListener('click', this.onClick.bind(this))
        this.render()
      }

      get garage() {
        return window.ACGarage
      }

      /* Called by the dialog's back button, which is not inside this element. */
      back() {
        if (this.state.year) this.state.year = null
        else this.state.model = null
        this.render()
      }

      onClick(event) {
        var button = event.target.closest('[data-step]')
        if (!button) return
        event.preventDefault()
        var step = button.dataset.step
        if (step === 'model') {
          this.state = { model: button.dataset.value, year: null }
        } else if (step === 'year') {
          this.state.year = button.dataset.value
        } else if (step === 'trim') {
          /* add, not set: the garage holds more than one car now, and choosing a
             second must not quietly drop the first. */
          this.garage.add({
            id: button.dataset.id,
            name: button.dataset.name,
            model: this.state.model,
            year: this.state.year,
            trim: button.dataset.trim,
          })
          this.dispatchEvent(new CustomEvent('garage:picked', { bubbles: true }))
          return
        }
        this.render()
      }

      /* The numbered bar the owner designed, drawn with the shared step-bar
         component so it and the wheel visualiser read as one system.

         A step you have passed shows what you chose rather than what it was
         called: "F-150", not "VEHICLE". That is the whole reason the bar earns
         its height. It doubles as the breadcrumb, which is why there is no
         second one. */
      stepBar(model, year) {
        var at = model ? (year ? 2 : 1) : 0
        var steps = [
          { label: model ? model.name : t('step_vehicle', 'Vehicle') },
          { label: year ? String(year.year) : t('step_year', 'Year') },
          { label: model && model.step_label === 'Engine' ? t('step_engine', 'Engine') : model && model.step_label ? model.step_label : t('step_trim', 'Trim') },
        ]
        var html = ''
        steps.forEach(function (s, i) {
          if (i) {
            /* The segment running INTO the step you are on is the current one, so
               it breathes the way the wheel visualiser's does. That is the owner's
               ask and it is the right half to animate: the line between where you
               were and where you are is the part still in motion. */
            var seg = i < at ? ' is-done' : i === at ? ' is-current' : ''
            html += '<li class="step-bar__seg' + seg + '" aria-hidden="true"></li>'
          }
          var state = i < at ? ' is-done' : i === at ? ' is-current' : ''
          html +=
            '<li class="step-bar__step' + state + '"' + (i === at ? ' aria-current="step"' : '') + '>' +
            '<span class="step-bar__num">' + (i + 1) + '</span>' +
            '<span class="step-bar__label">' + esc(s.label) + '</span>' +
            '</li>'
        })
        return '<ol class="step-bar garage-picker__steps">' + html + '</ol>'
      }

      /* Years grouped under their generation, because sixteen bare year tiles is
         a wall and "14th gen" is how an F-150 owner actually thinks about which
         truck they have. A generation with no year in scope never reaches here:
         the generator drops it, so an empty heading cannot be rendered. */
      yearGroups(model) {
        var gens = model.generations || []
        var used = {}
        var html = ''
        gens.forEach(function (g) {
          var inGen = model.years.filter(function (y) {
            return y.year >= g.from && y.year <= g.to
          })
          if (!inGen.length) return
          inGen.forEach(function (y) {
            used[y.year] = true
          })
          html +=
            '<p class="garage-picker__group">' + esc(g.name) + '</p>' +
            '<div class="garage-picker__tiles">' + inGen.map(yearTile).join('') + '</div>'
        })
        /* Any year the generation table does not cover still has to be
           reachable. Silently dropping it would take a car off the site. */
        var loose = model.years.filter(function (y) {
          return !used[y.year]
        })
        if (loose.length) {
          html += '<div class="garage-picker__tiles">' + loose.map(yearTile).join('') + '</div>'
        }
        return html
      }

      render() {
        var tree = this.garage && this.garage.tree()
        if (!tree) {
          this.innerHTML = ''
          return
        }
        var self = this
        var model = tree.models.filter(function (m) {
          return m.key === self.state.model
        })[0]
        var year =
          model &&
          model.years.filter(function (y) {
            return String(y.year) === String(self.state.year)
          })[0]

        var body
        if (!model) {
          body =
            '<div class="garage-picker__models">' +
            tree.models
              .map(function (m) {
                return (
                  '<button type="button" class="garage-picker__model" data-step="model" data-value="' +
                  esc(m.key) + '">' +
                  '<span class="garage-picker__model-icon" data-model-icon="' + esc(m.key) + '"></span>' +
                  '<span class="garage-picker__model-name">' + esc(m.name) + '</span>' +
                  '<span class="garage-picker__model-years">' + esc(m.from) + ' &ndash; ' + esc(m.to) + '</span>' +
                  '</button>'
                )
              })
              .join('') +
            '</div>'
        } else if (!year) {
          body = '<div class="garage-picker__years">' + this.yearGroups(model) + '</div>'
        } else {
          body =
            '<div class="garage-picker__tiles garage-picker__tiles--trims">' +
            year.trims
              .map(function (tr) {
                return (
                  '<button type="button" class="garage-picker__tile" data-step="trim"' +
                  ' data-id="' + esc(tr.id) + '" data-name="' + esc(tr.name) + '"' +
                  ' data-trim="' + esc(tr.trim) + '">' +
                  esc(tr.trim || model.name) +
                  '</button>'
                )
              })
              .join('') +
            '</div>'
        }

        /* When the garage already has a car in it, say so: this is an addition,
           not a replacement, and a shopper adding a second vehicle should not have
           to guess what happens to the first. */
        var lead = ''
        var saved = this.garage && this.garage.list ? this.garage.list() : []
        if (saved.length) {
          lead = '<p class="garage-picker__lead">' + esc(t('adding_another', 'Adding another vehicle to your garage.')) + '</p>'
        }

        this.innerHTML =
          this.stepBar(model, year) + lead + '<div class="garage-picker__body">' + body + '</div>'
        this.paintModelIcons()

        this.dispatchEvent(
          new CustomEvent('garage:step', {
            bubbles: true,
            detail: { canGoBack: !!model },
          })
        )

        /* Focus moves to the step container, never onto a tile. Focusing the
           first tile read as the site having already chosen a Mustang before
           the shopper touched anything, because the focus style and the press
           style are near neighbours. The container announces the step change
           to assistive tech and a keyboard user tabs onto the first tile
           themselves; a touch user sees nothing chosen, which is the truth. */
        if (this.dataset.autofocus !== 'false') {
          var body = this.querySelector('.garage-picker__body')
          if (body) {
            body.setAttribute('tabindex', '-1')
            body.focus({ preventScroll: true })
          }
        }
      }

      /* The silhouettes come from the icon library in Liquid, cloned out of the
         templates the dialog carries, so there is no second copy of the artwork
         in JS to drift from the one every other icon uses. */
      paintModelIcons() {
        this.querySelectorAll('[data-model-icon]').forEach(function (slot) {
          var tpl = document.querySelector(
            'template[data-garage-model-icon="' + slot.dataset.modelIcon + '"]'
          )
          if (tpl) slot.appendChild(tpl.content.cloneNode(true))
        })
      }
    }
  )
}

/* Storefront strings live in locales/en.default.json and reach JS through the
   data attributes the dialog carries, so this file never becomes a second place
   where copy is written. The fallback is only for a page that somehow rendered
   without the dialog. */
function t(key, fallback) {
  var host = document.querySelector('[data-garage-strings]')
  var value = host ? host.getAttribute('data-' + key.replace(/_/g, '-')) : null
  return value || fallback
}

/* One year tile, used by both the generation groups and the fallback list, so
   the two cannot render differently. */
function yearTile(y) {
  return (
    '<button type="button" class="garage-picker__tile" data-step="year" data-value="' +
    esc(y.year) + '">' + esc(y.year) + '</button>'
  )
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  })
}
